import { Suspense } from "react";
import { Link } from "react-router";
import { Spinner } from "../../../../../design-system/utils/Spinner/Spinner.jsx";
import { ImgForPath } from "../../../../../lib/images.jsx";
import { workshopStartStyles } from "../../../../admin/workshops/workshops-styles.css.js";
import AvatarGroup from "../../../../../design-system/components/AvatarGroup/AvatarGroup.jsx";
import { TeamMemberNames } from "../TeamMemberNames/TeamMemberNames.jsx";
import OtherFormFields from "../OtherFormFields/OtherFormFields.jsx";
import DescriptionWithNewLines from "../DescriptionWithNewLines/DescriptionWithNewLines.jsx";
import { shrink } from "../../../../../lib/shrink.js";
import { ReactionSummary } from "../ShowroomTileReactionSummary/ShowroomTileReactionSummary.jsx";
import { pluralize } from "../../../../../lib/text-utils.js";
import { Bubble } from "../../../../../design-system/components/Bubble/Bubble.jsx";
import styles from "./showroomEntryPreview.css.ts";

/**
 * Represents a preview component for a showroom entry.
 *
 * @param {Object} props - The props for the component.
 * @param {Object} props.entry - The showroom entry data.
 * @param {string} props.to - The URL to navigate to.
 * @param {number} props.unseenCount - The count of unseen entries.
 * @param {Object} props.workshopSettings - The settings for the workshop.
 * @returns {JSX.Element} The rendered preview component.
 */
export const ShowroomEntryPreview = ({
  entry,
  to,
  unseenCount,
  workshopSettings,
}) => {
  const { title, description, upload, coverImage } = entry.formData;
  const coverFilePath =
    coverImage?.path ||
    coverImage?.data?.path ||
    (upload?.data?.probe?.width ? upload.data.path : null);

  const Header = coverFilePath ? (
    <ImgForPath
      path={coverFilePath}
      height={500}
      width={500}
      className={workshopStartStyles.coverImg}
      style={{ height: "15rem" }}
    />
  ) : (
    <div style={{ height: "1.5rem" }} />
  );

  return (
    <>
      {unseenCount > 0 && <Bubble text={String(unseenCount)} />}
      <Suspense fallback={<Spinner />}>
        <Link to={to} className={styles.box}>
          {Header}
          <div className={styles.boxBody}>
            <header className={styles.headerColumn}>
              {entry.teamMembers && !!entry.teamMembers.length && (
                <AvatarGroup users={entry.teamMembers} />
              )}
              <h3 className={styles.boxHeadline}>{title}</h3>
              <p>
                Projekt von{" "}
                <TeamMemberNames users={entry.teamMembers} small={true} />
              </p>
            </header>
            <div className={styles.sectionSpacing}>
              <OtherFormFields formData={entry.formData} styles={styles} />

              <DescriptionWithNewLines
                description={shrink(description, 200)}
                paragraphStyle={styles.fieldValue}
                labelStyle={styles.label}
              />
            </div>
            <div className={styles.reactionBar}>
              <ReactionSummary counts={entry.reactionCounts} />

              {workshopSettings &&
                workshopSettings.commentsEnabled &&
                entry.commentCount > 0 && (
                  <p>
                    {pluralize(entry.commentCount, "Kommentar", "Kommentare")}
                  </p>
                )}

              {entry.visitCounts > 0 && (
                <p>{pluralize(entry.visitCounts, "Besuch", "Besuche")}</p>
              )}
            </div>
          </div>
        </Link>
      </Suspense>
    </>
  );
};
